import { computed, inject, Inject, Injectable, Signal } from '@angular/core';
import { ModalController, Platform } from '@ionic/angular';
import { IClientSocket, WsNamespaces, PlatformType, PlugType, CarFilters, IVersionUpdate, IUserLanguage } from 'common_library';
import { BehaviorSubject, filter, first, fromEvent, map, Subject, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { _ } from '../consts';
import { SocketIoService } from './communication/socket-io.service';
import { StoreService } from './utils/store.service';
import { Geolocation, Geoposition } from '@awesome-cordova-plugins/geolocation/ngx';
import { RxState } from '@rx-angular/state';
import { GLOBAL_RX_STATE, GlobalState } from '../app-state.tokens';
import { HttpIoService } from './communication/http-io.service';
import { AppNativeService } from './app.native.service';
import { ToastService } from './utils/toast.service';
import { Router } from '@angular/router';
import { AppInfoComponent } from '../components/master-details-app-info/app-info.component';
import { ModalChapterUpdatedComponent } from '../components/modal-chapter-updated/modal-chapter-updated.component';
import { TranslateService } from '@ngx-translate/core';
import { MainState } from '../types/state/app-main-state.interface';
import { MainStateService } from './state/app-main-state.service';
import { Point } from 'geojson';
import { GeoLocationNativeService } from './native/geo-location-native.service';
import { CpoFilterOptionsService } from './cpo-filter-options.service';
import { DEFAULT_FILTER } from '../pages/filter/filter.page';
import { take } from 'lodash';
import { Socket } from 'socket.io-client';
import { Capacitor } from '@capacitor/core';
import { Haptics, NotificationType as HapticsNotificationType } from '@capacitor/haptics';
import { TranslateConfigService } from './translate.service';

const HEARTBEAT_MS = 300000; // 5 minuti

export enum ModalSource {
  Login = 'login',
  Registration = 'registration',
}

@Injectable({
  providedIn: 'root',
})
export class AppService {

  public apiUrl: string;

  public readonly returnUrl: string | null;
  visible$ = new BehaviorSubject<boolean>(true);
  checkForNativeUpdates$ = new BehaviorSubject<boolean>(false);
  $filterState: Signal<CarFilters> = this.mainStateService.$filterButtonState;

  private _hasGeoloc = false;
  get hasGeoloc(): boolean { return this._hasGeoloc; }

  waitingToastData: BehaviorSubject<{
    isProgression: boolean,
    message?: string,
    duration?: number
  }> = new BehaviorSubject({ isProgression: false });
  outOfView: boolean = false;
  translateService = inject(TranslateService);

  $login = new Subject<void>();
  $registration = new Subject<void>();


  $text = computed(() => { // Questa decisione non mi piace, prima o poi la cambio :(
    const filter = this.$filterState() || DEFAULT_FILTER;
    if (filter.plugs.length === 0) {
      return 'APP.FILTER.CONNECTOR';
    }
  });

  constructor(
    @Inject(GLOBAL_RX_STATE) private globalState: RxState<GlobalState>,
    private HIO: HttpIoService,
    public platform: Platform,
    public SIS: SocketIoService,
    @Inject('APP_START_LOCATION') private appStartLocation: string,

    private store: StoreService,
    public geolocation: Geolocation,
    private geoLocationNative: GeoLocationNativeService,
    private appNativeService: AppNativeService,
    private mainStateService: MainStateService,
    private cpoFilterOptionsService: CpoFilterOptionsService,
    private toast: ToastService,
    private modalCtrl: ModalController,
    private router: Router,
    private translate: TranslateService,
    private translateConfigService: TranslateConfigService,
  ) {

    // ###########################################################################################################################
    // aggiorno un proprietà dello stato globale che mi informa se l'app è visibile oppure no
    const visible = fromEvent(document, 'visibilitychange').pipe(map((event) => document.visibilityState === 'visible'));
    this.globalState.connect("visible", visible);

    // ###########################################################################################################################
    // PROCEDURA DI PULIZIA DELL'APP SE RISVEGLIATA DA UN PERIODO DI INATTIVITA'
    // nel mobile i tab del browser possono essere congelati o riesumati per salvare risorse di cpu e memoria
    // quando viene riesumata una web app spesso va in crisi
    // con questo controllo ci accorgiamo che l'app è appena stata riesumata e la facciamo ripartire da zero (window.location.reload)
    let ts = new Date().getTime();
    setInterval(() => {
      if ((new Date().getTime() - ts) > 2 * HEARTBEAT_MS) {
        console.log('[${NAME}] Wake-up from sleep detected');
        window.location.reload();
      }
      ts = new Date().getTime();
    }, HEARTBEAT_MS);
    // ###########################################################################################################################

    const backOnlineEvent$ = this.globalState.select("mainState").pipe(filter(ms => ms === MainState.ONLINE));

    // Ogni volta che torno online aggiorno l'appVersion (in effetti alcune volte torno online perchè è stato fatto un aggiornamento)
    backOnlineEvent$.pipe(
      tap(async () => {
        try {
          let versionUpdate = await this.HIO.get<IVersionUpdate>('versionUpdate');
          versionUpdate.frontendVersion = _.VERSION;
          if (this.mainStateService.getNative()) {
            try {
              versionUpdate.frontendVersion = await this.appNativeService.getCurrentAppVersionName();
            } catch (ex) {
              console.log("Errore version update:", ex);
            }
          }
          this.mainStateService.setVersion(versionUpdate);
          const precVersion = this.store.get<string>(_.VERSION_KEY);
          if (versionUpdate.frontendVersion !== precVersion) {
            this.store.set(_.VERSION_KEY, versionUpdate.frontendVersion);
            await this.toast.presentBasicToast({ icon: "cloud-done-outline", header: this.translate.instant("APP.SOFTWARE_UPDATE.NEW_VERSION"), message: this.translate.instant("APP.SOFTWARE_UPDATE.APP_UPDATED_TO_VERSION", { appVersion: versionUpdate.frontendVersion }), color: 'tertiary' });
          }

        } catch (ex) {
          console.log("Error during check min supported version", ex);
        }
      })
    ).subscribe()

    this.SIS.connectedNamespaces.get(WsNamespaces.Client).subscribe((connected) => {
      if (connected)
        this.SIS.sendMessage<IClientSocket.Dto.HandshakeData>(
          WsNamespaces.Client,
          IClientSocket.ClientEvents.Handshake,
          { clientId: this.mainStateService.getClientId() }
        );
    });
    // this.SIS.fromEvent<any>(
    //   WsNamespaces.Client,
    //   IClientSocket.ServerEvents.GeneralNotification
    // ).subscribe(async (notification: any) => {
    //   console.log('AppService : general_notification', notification);
    //   // if (notification.type === 'update') {
    //   //   this.checkForNativeUpdates$.next(true);
    //   //   const { title, body } = notification;
    //   //   await this.toast.presentBasicToast({ icon: "cloud-done-outline", header: title, message: body, color: 'tertiary' });
    //   // }
    // });
    // this.SIS.listen<any>(
    //   WsNamespaces.Client,
    //   IClientSocket.ServerEvents.GeneralNotification
    // ).subscribe((notification: any) => {
    //   console.log('AppService : general_notification', notification);
    //   // this.checkForNativeUpdates$.next(true);
    //   // const { title, body } = notification;
    //   // this.toast.presentBasicToast({ icon: "cloud-done-outline", header: title, message: body, color: 'tertiary' });
    // });
  }

  // questo metodo va chiamato solo dall'ngOnInit dell'app.component (una sola volta ovviamente)
  // successivamente tutte le propietà calcolate possono essere ustate/valutate solo successivamente all'inizializzazione
  async init() {
    try {
      console.log("inizializzazione ...");

      const clientId = this.mainStateService.getClientId();
      const native = this.mainStateService.getNative();

      const rv = await this.platform.ready();
      const platforms = this.platform.platforms();
      const agent = navigator.userAgent;

      console.log("CLIENT:", clientId, "PLATFORM READY:", rv, "PLATFORM:", platforms);
      console.log("agent", agent);

      this.checkNotNativePlatform(platforms, agent);

      if (native) {
        await this.appNativeService.init();
      }
      else {
        try {
          const pos = await this.geolocation.getCurrentPosition(({
            enableHighAccuracy: true,
          }));
          this._hasGeoloc = !!pos?.timestamp;

        } catch (err) {
          console.error("Errore inizializzazione localizzazione app non nativa", err);
        }
      }
      await this.initNotificationSocket();

    } catch (err) {
      console.error("Errore inizializzazione", err);
    }
    console.log("inizializzazione terminata");
    this.mainStateService.setInitializationCompleted(true);


    // Ogni minuto (se la localizzazione è attiva) mi segno la positione del client, in modo da averla già pronta qualora dovesse servire per avviare una sessione
    // Se lo faccio all'occorrenza, infatti, posso avere un ritardo elevato dovuto al tempo necessario per ottenere la posizione
    let self = this;
    setInterval(async () => {
      if (this.isLocationEnabled()) {
        await self.saveCurrentPosition();
      }
    }, 30000)
  }

  get isProduction(): boolean {
    return environment.production;
  }

  checkNotNativePlatform(platforms: string[], agent: string) {
    let isIos = false;
    let isAndroid = false;

    try {
      isIos = !!platforms ? platforms.includes('ios') : agent.indexOf('iPhone') > 0;
      isAndroid = !!platforms ? platforms.includes('android') : agent.indexOf('Android') > 0;
    } catch (err) { }

    if (isIos) {
      if (!this.mainStateService.getNative()) {
        this.mainStateService.setPlatformType(PlatformType.iOSMobileWeb);
      }
    } else if (isAndroid) {
      if (!this.mainStateService.getNative()) {
        this.mainStateService.setPlatformType(PlatformType.AndroidMobileWeb);
      }
    } else {
      this.mainStateService.setPlatformType(PlatformType.Desktop);
    }
  }

  showWaitingToast(message?: string, duration?: number) {
    this.waitingToastData.next({
      isProgression: true,
      message: message ? message : 'Attendere il completamento dell\'operazione.',
      duration: duration ? duration : 120
    });
  }

  hideWaitingToast() {
    this.waitingToastData.next({ isProgression: false });
  }

  async showAppInfo(chapterId?: string, source?: ModalSource) {
    if (this.modalCtrl && source !== ModalSource.Registration) this.modalCtrl.dismiss();
    const modal = await this.modalCtrl.create({ component: AppInfoComponent, cssClass: 'yf-master-detail-modal', componentProps: { selectedChapterId: chapterId ? chapterId : undefined } });
    await modal.present();
    await this.router.navigate([], {
      queryParams: { open_app_info: null },
      queryParamsHandling: 'merge',
    });
  }

  async showSingleChapter(chapterId?: string, saveToLocalSotrage: boolean = false, logoutOnDenial = true) {
    if (this.modalCtrl) this.modalCtrl.dismiss();
    const modal = await this.modalCtrl.create(
      {
        component: ModalChapterUpdatedComponent,
        backdropDismiss: false,
        componentProps: { selectedChapterId: chapterId ? chapterId : undefined, saveToLocalSotrage, logoutOnDenial }
      },);
    await modal.present();
  }

  notEmpty(obj: any): boolean {
    if (obj === null || obj === undefined) {
      return false;
    }
    if (Array.isArray(obj)) {
      return obj.length > 0;
    }
    if (typeof obj === 'object') {
      return Object.values(obj).some(value => this.notEmpty(value));
    }
    return obj !== null;
  }

  private async saveCurrentPosition() {
    const coord = await this.getCurrentPosition();
    this.mainStateService.setClientLastPosition(coord);
  }

  async getCurrentPosition(): Promise<Point> {
    let coord: Point = {
      type: 'Point',
      coordinates: [0, 0]
    };

    try {
      let geopos: Geoposition;
      if (this.mainStateService.getNative()) {
        if (this.geoLocationNative.permissionState?.location !== 'granted') {
          return coord;
        }
        geopos = await this.geoLocationNative.getCurrentPosition();
      } else {
        geopos = await this.geolocation.getCurrentPosition();
      }

      if (geopos) {
        coord = { ...coord, coordinates: [geopos.coords.longitude, geopos.coords.latitude] }
      }
    } catch (ex) {
      console.log(ex);
    }
    return coord;
  }

  isLocationEnabled(): boolean {
    if (this.mainStateService.getNative()) {
      return this.geoLocationNative.hasGeoloc;
    }
    else {
      return this.hasGeoloc;
    }
  }

  async setStartingFilterState() {
    let plugs = DEFAULT_FILTER.plugs;
    let range = DEFAULT_FILTER.range;
    let cpos = DEFAULT_FILTER.cpos;
    let operatorIds = DEFAULT_FILTER.operatorIds;
    let savedFilter = this.store.get<CarFilters>(_.SELECTED_FILTER);
    if (savedFilter) {
      plugs = savedFilter.plugs || DEFAULT_FILTER.plugs;
      range = savedFilter.range || DEFAULT_FILTER.range;
      cpos = savedFilter.cpos || DEFAULT_FILTER.cpos;
      operatorIds = savedFilter.operatorIds || DEFAULT_FILTER.operatorIds;
    }
    let filter: CarFilters = this.cpoFilterOptionsService.normalizeFilterState({
      plugs,
      range,
      quick: [],
      carId: null,
      cpos,
      operatorIds,
    });
    this.mainStateService.setFilterButtonState(filter);
  }

  async initNotificationSocket() {
    try {
      const clientNs = WsNamespaces.Client;
      const socket = this.SIS.sockets[clientNs];

      if (!socket) {
        console.error(`[SOCKET] No socket for namespace ${clientNs}`);
        return;
      }

      console.log(`[SOCKET] Client socket connected: ${socket.connected}, ID: ${socket.id}`);

      if (!socket.connected) {
        console.log(`[SOCKET] Attempting to connect client socket`);
        socket.connect();

        setTimeout(() => {
          if (socket.connected) {
            console.log(`[SOCKET] Socket now connected with ID: ${socket.id}`);
            this.registerSocketForNotifications(socket);
          } else {
            console.error(`[SOCKET] Socket still not connected`);
          }
        }, 1000);
      } else {
        this.registerSocketForNotifications(socket);
      }

    } catch (error) {
      console.error('[SOCKET] Test error:', error);
    }
  }

  private registerSocketForNotifications(socket: Socket): void {
    try {
      console.log(`[SOCKET] Registering for GeneralNotification`);

      socket.off(IClientSocket.ServerEvents.GeneralNotification);

      socket.on(IClientSocket.ServerEvents.GeneralNotification, (data: {
        ionicIcon: string;
        title: string;
        body: string;
      }, ack) => {
        this.handleServerNotification(data);
        if (typeof ack === 'function') {
          try {
            ack({ success: true });
          } catch (e) {
          }
        }
      });

    } catch (error) {
      console.error('[SOCKET] Registration error:', error);
    }
  }

  async handleServerNotification(notification: {
    ionicIcon: string;
    title: string;
    body: string;
  }) {
    if (!notification || !notification.title || !notification.body) {
      return;
    }
    await this.vibrateForNotification();
  }

  private async vibrateForNotification(): Promise<void> {
    try {
      await Haptics.notification({ type: HapticsNotificationType.Success });
      if (Capacitor.getPlatform() === 'android') {
        await Haptics.vibrate({ duration: 250 });
      }
    } catch (error) {
      console.warn('[SOCKET] Unable to vibrate for notification:', error);
    }
  }
}
